import darkThemeColors from './colors/darkThemeColors.mjs';
import { keyframes } from 'styled-components';

var Size;
(function (Size) {
    Size["Regular"] = "regular";
    Size["Medium"] = "medium";
    Size["Small"] = "small";
    Size["Tiny"] = "tiny";
    Size["Micro"] = "micro";
    Size["Nano"] = "nano";
})(Size || (Size = {}));
var FontWeight;
(function (FontWeight) {
    FontWeight["Thin"] = "thin";
    FontWeight["Extralight"] = "extralight";
    FontWeight["Light"] = "light";
    FontWeight["Regular"] = "regular";
    FontWeight["Medium"] = "medium";
    FontWeight["Semibold"] = "semibold";
    FontWeight["Bold"] = "bold";
    FontWeight["Black"] = "black";
    FontWeight["Super"] = "super";
})(FontWeight || (FontWeight = {}));
const progress = keyframes `
  0% {
    width: 0;
  }
  100% {
    width: 1;
  }
`;
var theme = {
    colors: darkThemeColors,
    gradients: {
        imageOverlay: `linear-gradient(180deg, transparent 29.66%, ${darkThemeColors.slate} 89.13%)`,
        videoOverlay: `linear-gradient(180deg, ${darkThemeColors.slate} 0%, ${darkThemeColors.slate} 105.89%)`,
        videoControlBarOverlay: `linear-gradient(180deg, ${darkThemeColors.slate}00 0%, ${darkThemeColors.slate}80 100%)`,
        horizontalCarousel: {
            prevButton: `linear-gradient( 270deg, ${darkThemeColors.slate}00 0%, ${darkThemeColors.slate}96 37.98%, ${darkThemeColors.slate}cf 100% )`,
            nextButton: `linear-gradient( 270deg, ${darkThemeColors.slate}cf 0%, ${darkThemeColors.slate}96 63.02%, ${darkThemeColors.slate}00 100% )`
        },
        horizontalList: {
            nextOverlay: `linear-gradient(270deg, ${darkThemeColors.slate} 0%, ${darkThemeColors.slate}00 100%)`,
            prevOverlay: `linear-gradient(90deg, ${darkThemeColors.slate} 0%, ${darkThemeColors.slate}00 100%)`
        }
    },
    breakpoints: {
        xxs: '0',
        xs: '375px',
        sm: '576px',
        md: '768px',
        lg: '992px',
        xl: '1200px',
        xxl: '1400px',
        max: '1920px'
    },
    typography: {
        fontFamily: 'Graphik, sans-serif',
        fontStyle: { normal: 'normal', italic: 'italic' },
        textTransform: { none: 'none', uppercase: 'uppercase' },
        fontWeight: {
            thin: 100,
            extralight: 200,
            light: 300,
            regular: 400,
            medium: 500,
            semibold: 600,
            bold: 700,
            black: 800,
            super: 900
        },
        headings: {
            h1: {
                fontSize: '2.5rem',
                lineHeight: '3rem'
            },
            h2: {
                fontSize: '1.75rem',
                lineHeight: '2.25rem'
            },
            h3: {
                fontSize: '1.25rem',
                lineHeight: '1.5rem'
            },
            h4: {
                fontSize: '1rem',
                lineHeight: '1.3rem'
            },
            h5: {
                fontSize: '1.125rem',
                lineHeight: '1.125rem'
            },
            h6: {
                fontSize: '0.875rem',
                lineHeight: '1.125rem',
                textTransform: 'uppercase'
            }
        },
        text: {
            regular: { fontSize: '1.25rem', lineHeight: '1.87rem' },
            medium: { fontSize: '1.125rem', lineHeight: '1.438rem' },
            small: { fontSize: '1rem', lineHeight: '1.3rem' },
            smallSpaced: { fontSize: '1rem', lineHeight: '1.3rem' },
            tiny: { fontSize: '0.875rem', lineHeight: '1.138rem' },
            micro: { fontSize: '0.75rem', lineHeight: '0.975rem' },
            nano: { fontSize: '0.625rem', lineHeight: '0.8125rem' }
        },
        interactive: {
            regular: {
                fontWeight: 500,
                fontSize: '1.125rem',
                lineHeight: '1.5rem'
            },
            medium: {
                fontWeight: 500,
                fontSize: '1rem',
                lineHeight: '1.3rem'
            },
            small: {
                fontWeight: 500,
                fontSize: '0.875rem',
                lineHeight: '1.125rem'
            },
            tiny: {
                fontWeight: 500,
                fontSize: '0.75rem',
                lineHeight: '1rem'
            }
        }
    },
    interactive: {
        button: {
            regular: {
                fontSize: '1.125rem',
                lineHeight: '1.5rem',
                padding: {
                    md: { basic: '11px 32px 9px', symmetrical: '15px 16px 13px' },
                    sm: { basic: '11px 32px 9px', symmetrical: '13px 16px 11px' }
                },
                gap: '8px',
                transform: 'none'
            },
            small: {
                fontSize: '0.75rem',
                lineHeight: '1rem',
                padding: {
                    md: { basic: '5px 32px 3px', symmetrical: '3px 3px 1px' },
                    sm: { basic: '5px 32px 3px', symmetrical: '3px 3px 1px' }
                },
                gap: '2px',
                transform: 'scale(0.7)'
            }
        },
        iconButton: {
            regular: {
                transform: 'none'
            },
            small: {
                transform: 'scale(0.7)'
            }
        }
    },
    zIndex: {
        negative: -1,
        base: 0,
        minor: 1,
        dropdown: 10,
        sticky: 20,
        overlay: 30,
        modal: 40,
        alert: 50
    },
    animation: {
        duration: {
            instant: '0ms',
            quick1: '80ms',
            quick2: '120ms',
            moderate1: '180ms',
            moderate2: '260ms',
            gentle1: '320ms',
            gentle2: '400ms',
            medium: '1500ms',
            long: '2000ms'
        },
        easing: {
            productive: {
                standard: 'cubic-bezier(0.2, 0, 0.38, 0.9)',
                entrance: 'cubic-bezier(0, 0, 0.38, 0.9)',
                exit: 'cubic-bezier(0.2, 0, 1, 0.9)'
            },
            expressive: {
                standard: 'cubic-bezier(0.4, 0.14, 0.3, 1)',
                entrance: 'cubic-bezier(0, 0, 0.3, 1)',
                exit: 'cubic-bezier(0.4, 0.14, 1, 1)'
            }
        }
    },
    keyframes: {
        progress
    }
};

export { FontWeight, Size, theme as default };
//# sourceMappingURL=theme.mjs.map
